import { useEffect, useState } from "react";
import styles from "./PlansTable.module.scss";
import { useStore } from "../../../stores/use_global_state";
import { getAccountData, getCurrency, getPayAsGoId, getPlans, getSubscribeUrl } from "../../../pages/api/api";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import * as ga from "../../../lib/ga";
import PlansTypeSwitch from "./Switch/Switch";
import RadioInput from "../RadioInput/RadioInput";
import { cloudPaymentsDefaultData, cloudPaymentsPayAsGoText, cloudPaymentsSubscriptionText, cloudPaymentsThreeMonthSubscriptionText, initCurrencyData } from "./const";
import FreeTable from "./FreeTable/FreeTable";
import ModalWrapper from "../../illustration-service/ServiceEditor/ModalWrapper/ModalWrapper";
const DynamicCurrencySelectionModal = dynamic(() => import("./CurrencySelectionModal/CurrencySelectionModal"), { ssr: false });
const DynamicAuthModal = dynamic(() => import("../../AuthModal/AuthModal"), { ssr: false });

const ContactUsBtn = ({ classes, dictionary }) => {
	return (
		<a href="mailto:support@bgrem.ai" className={classes}>
			{dictionary["Contact us"] || "Contact us"}
		</a>
	);
};

function PlansTable({ dictionary, initialPlans, locale, initialProPlan, initialAnnually }) {
	const payAsGoData = initialPlans.filter((item) => item.period === "onetime");
	const router = useRouter();
	const { query } = router;
	const [currency, setCurrency] = useState(query.currency ? initCurrencyData[query.currency] : { symbol: "$", text: "USD" });
	const [plans, setPlans] = useState([...initialPlans]);
	const { state, dispatch } = useStore();
	const { load, userInfo, accountData } = state;
	const [activeSubscription, setActiveSubscription] = useState(initialProPlan);
	const [payAsGo, setPayAsGo] = useState(payAsGoData[2]);
	const [isAnnuallyOn, setIsAnnuallyOn] = useState(initialAnnually);
	const [paymentInRubles, setPaymentInRubles] = useState(locale !== "ru" ? false : true);
	const [currencySelectionModalIsOpen, setCurrencySelectionModalIsOpen] = useState(false);
	const [disableBtn, setDisableBtn] = useState(false);
	const [openAuthModal, setOpenAuthModal] = useState(false);
	const isRu = locale === "ru";

	useEffect(
		() => {
			const determineLocation = async () => {
				if (load) {
					let currencyType = await getCurrency();
					if (query.currency) currencyType = query.currency;
					if (accountData?.subscription?.currency) currencyType = accountData?.subscription?.currency;
					const currencyPlans = await getPlans(currencyType);
					setPlans(currencyPlans.data);
					let locationCurrency = currencyType === "RUB" ? initCurrencyData[accountData?.subscription?.currency || (await getCurrency())] : initCurrencyData[currencyType];
					if (locationCurrency === undefined) locationCurrency = { symbol: currencyType, text: currencyType };
					setCurrency(isRu && (currencyType === "USD" || currencyType === "RUB") ? { ...initCurrencyData["RUB"], locationCurrency } : locationCurrency);
				}
			};

			determineLocation();
		},
		// eslint-disable-next-line
		[load, locale, accountData]
	);

	useEffect(
		() => {
			const activePeriod = isAnnuallyOn ? "yearly" : "monthly";
			const activeProPlansArr = plans.filter((item) => item.period === activePeriod);
			setActiveSubscription(activeProPlansArr[2]);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isAnnuallyOn, plans]
	);

	const gaSubscribeClick = (plan, userPlan) => {
		ga.event({ action: `Pricing page: click subscribe ${plan} plan`, params: { event_category: `Current plan: ${userPlan}` } });
	};

	const subscribe = async (id, credits = false, price = false, itsPayAsGo = false, period = 0) => {
		const callback_url = query.callback_url !== null && query.callback_url !== undefined ? `${query.callback_url}?${query.callback_url_query}` : null;

		// проверка на то является ли пользователь зарегистрированным и перенаправление на авторизацию/регистрацию:
		if (load && userInfo === null) {
			ga.event({ action: "unregistered user wanted to subscribe and redirected to sign in", params: { event_category: "Pricing" } });
			setOpenAuthModal(true);
			return;
		}

		// проверка на то использует ли пользователь русскоязычную версию сайта, для открытия модального окна выбора валюты:

		if (locale === "ru" && (currency.text === "USD" || currency.text === "RUB") && currencySelectionModalIsOpen === false) {
			setCurrencySelectionModalIsOpen({ ...{ id, credits, price, itsPayAsGo, period: query.period ? Number(query.period) : 0 } });
			return;
		}

		// если пользователь выбрал оплату в рублях, то открывается виджет cloudPayments для оплаты тарифа:

		setCurrencySelectionModalIsOpen(false);
		if (paymentInRubles && locale === "ru" && (currency.text === "USD" || currency.text === "RUB")) {
			ga.event({ action: `Pricing page: user go to cloudPayments`, params: { event_category: "Pricing" } });

			var receipt = {
				'Items': [
					{
						'label':
							period > 0
								? cloudPaymentsThreeMonthSubscriptionText(credits)
								: itsPayAsGo
									? cloudPaymentsPayAsGoText(credits)
									: cloudPaymentsSubscriptionText(credits, isAnnuallyOn),
						'price': Number(price),
						'quantity': 1,
						'amount': Number(price),
						'method': 0,
						'object': 4,
					}
				],
				'taxationSystem': 2,
				'calculationPlace': 'https://bgrem.ai',
				'email': userInfo.userData.email,
				"amounts": { "electronic": Number(price) }
			};

			const CloudPaymentsData = itsPayAsGo ? {
				CloudPayments: {
					CustomerReceipt: receipt,
				}
			} : {
				CloudPayments: {
					CustomerReceipt: receipt,
					recurrent: {
						interval: "Month",
						period: period > 0 ? period : isAnnuallyOn ? 12 : 1,
						customerReceipt: receipt
					}
				}
			};

			const payment = {
				//options
				publicId: process.env.NEXT_PUBLIC_CLOUDPAYMENTS, //id из личного кабинета
				description: period > 0 ? cloudPaymentsThreeMonthSubscriptionText(credits) : itsPayAsGo ? cloudPaymentsPayAsGoText(credits) : cloudPaymentsSubscriptionText(credits, isAnnuallyOn), //назначение
				amount: Number(price), //сумма
				accountId: userInfo.userData.user_id, //идентификатор плательщика (необязательно)
				email: userInfo.userData.email, //email плательщика (необязательно)
				...cloudPaymentsDefaultData,
				data: {
					...CloudPaymentsData,
					planId: itsPayAsGo === true
						? null
						: id,
					paymentId: itsPayAsGo === true
						? id
						: null,
					credits: credits
				},
			};
			const widget = new cp.CloudPayments();
			widget.pay(
				"charge", // 'auth' или 'charge'
				{ ...payment },
				{
					onSuccess: function (e) {
						console.log(e);
						setTimeout(async () => {
							let { data } = await getAccountData(userInfo.token);

							let restrictions = state.restrictions.map((item) => {
								item.active = item.status === data.plan;
								return item;
							});

							dispatch({ type: "setAccountData", accountData: data, restrictions: restrictions });
							callback_url !== null ? router.push(callback_url) : router.push(`/?workspace=profile&survey=payment&currency="RUB"&amount:${Number(price)}`);
						}, 1000);

						// success
						// добавить сообщение об успешной оплате
					},
					onComplete: function (e) {
						console.log(e);
					},
					onFail: function (reason, options) {
						// console.clear();
						console.log(reason);
						console.log(options);

						// fail
						// добавить сообщение об ошибке
					},
				}
			);
			return;
		}

		// получение ссылки и переход по ней для оплаты тарифа:
		setDisableBtn(true);
		const { data } = await getSubscribeUrl(id, credits, userInfo?.token, currency.text === "RUB" ? "USD" : currency.text, callback_url);
		const { url } = data;
		window.location.href = url;
		setDisableBtn(false);
		setCurrencySelectionModalIsOpen(false);
	};

	const getProDataForPeriod = (allPlans, isAnnually) => allPlans.filter((item) => item.period === (isAnnually ? "yearly" : "monthly"));

	useEffect(
		() => {
			if (query.method === undefined) return;

			if (query.method.toLowerCase() === "payAsGo".toLowerCase()) {
				if (locale === "ru") {
					setCurrencySelectionModalIsOpen({
						price: query.price,
						amount: { RUB: query.amountRUB, USD: query.amountUSD },
						credits: query.credits,
						token: userInfo.token,
						period: query.period ? query.period : 0,
					});
					return;
				}

				const goToUSDUrl = async () => {
					const response = await getPayAsGoId(query.currency === "RUB" ? "USD" : query.currency, query.amountUSD, userInfo.token);
					const { url } = response.data;
					ga.event({
						action: `Pricing page: user go to stripe`,
						params: {
							event_category: "Pricing",
							event_callback: () => {
								window.location.href = url;
								setCurrencySelectionModalIsOpen(false);
							},
						},
					});
				};

				goToUSDUrl();
				return;
			}

			// переделать это и запихнуть в серверсайд
			const activePlan = plans.find((item) => Number(item.id) === Number(query.plan));
			setIsAnnuallyOn(false);
			setActiveSubscription(activePlan);
			subscribe(activePlan?.id, activePlan?.credits, activePlan?.prices.RUB);
		},
		//eslint-disable-next-line react-hooks/exhaustive-deps
		[query]
	);

	const price = (activeItem) => (isRu ? activeItem.prices[currency.text].toFixed(2) + currency.symbol : currency.symbol + activeItem.prices[currency.text].toFixed(2));
	const monthPrice = (activeItem) => (isRu ? (activeItem.prices[currency.text] / 12).toFixed(2) + currency.symbol : currency.symbol + (activeItem.prices[currency.text] / 12).toFixed(2));

	const notFreeUserProSubmit = () => {
		setOpenNotFreeUserModal(false);

		subscribe(activeSubscription.id, activeSubscription.credits, activeSubscription.prices.RUB);
		gaSubscribeClick("pro", state?.accountData?.plan || "nonAuth");
	};

	const notFreeUserPayAsGoSubmit = () => {
		setOpenNotFreeUserModal(false);

		subscribe(payAsGo.id, payAsGo.credits, payAsGo.prices.RUB, "plan");
		gaSubscribeClick("payAsGo", state?.accountData?.plan || "nonAuth");
	};

	const [openNotFreeUserModal, setOpenNotFreeUserModal] = useState(false);

	const proSubmit = () => {
		if (!accountData) { return setOpenAuthModal(true); }


		if (accountData?.plan !== "free") {
			setOpenNotFreeUserModal("pro");
			return;
		}

		subscribe(activeSubscription.id, activeSubscription.credits, activeSubscription.prices.RUB);
		gaSubscribeClick("pro", state?.accountData?.plan || "nonAuth");
	};

	const payAsGoSubmit = () => {
		if (!accountData) { return setOpenAuthModal(true); }

		if (accountData?.plan !== "free") {
			setOpenNotFreeUserModal("payAsGo");
			return;
		}

		subscribe(payAsGo.id, payAsGo.credits, payAsGo.prices.RUB, "plan");
		gaSubscribeClick("payAsGo", state?.accountData?.plan || "nonAuth");
	};

	const RadioInputProps = { locale, dictionary, currency, isAnnuallyOn };

	return (
		<>
			<section className={styles[locale]} style={{ position: "relative" }}>
				<div className={styles.subscription_wrapper}>
					<FreeTable {...{ styles, currency, isRu, dictionary }}>
						<button className={`${styles.button} ${styles.stroke} ${styles.free}`} disabled={userInfo !== null} onClick={() => setOpenAuthModal(true)}>
							{userInfo !== "null" && state?.accountData?.plan === "free" ? dictionary["Current plan"] : dictionary["get_started"]}
						</button>
					</FreeTable>

					<div className={`${styles.subscription_item} ${styles.recommended}`} style={{ zIndex: 10 }}>
						<div className={`${styles.top}`}>
							<h2 className={styles.title}>{dictionary["subscription"] || "Subscription"}</h2>
							<PlansTypeSwitch {...{ dictionary, isAnnuallyOn, setIsAnnuallyOn }} />
						</div>

						<div id="pro-credit-cost" className={`${styles.price}`}>
							<div className={styles.priceValue}>
								{isAnnuallyOn && (
									<span className={styles.monthlyPrice}>
										{!isRu && currency.symbol} {plans.find((item) => item.period === "monthly" && item.credits * 12 === activeSubscription?.credits)?.prices[currency.text]} {isRu && currency.symbol}
									</span>
								)}
								{isAnnuallyOn ? monthPrice(activeSubscription) : price(activeSubscription)}
							</div>
							<div className={styles.priceText}>
								<span className={styles.per}>{dictionary["per_month"] || "per month"}</span>
								<span className={styles.description}>
									{isAnnuallyOn ? `${dictionary["billed_annually"] || "billed annually"} ${activeSubscription.prices[currency.text]}` : dictionary["billed_annually"] || "billed annually"}
								</span>
							</div>
						</div>
						<div className={styles.price_info}>
							{activeSubscription !== null && (
								<>
									<RadioInput {...{ list: getProDataForPeriod(plans, isAnnuallyOn), activeItem: activeSubscription, handleChange: setActiveSubscription, name: "Pro", ...RadioInputProps }} />
									{activeSubscription?.id !== "contactUsPro" ? (
										<button id="pro-submit-btn" className={styles.button} disabled={disableBtn} onClick={proSubmit}>
											{dictionary["subscribe"]}
										</button>
									) : (
										<ContactUsBtn classes={`${styles.button}`} dictionary={dictionary} />
									)}
								</>
							)}
							<p className={styles.infoMessage}>{dictionary["all_unused_credits"] || "All unused credits roll over to the next period up to 5 months of accumulation. Upgrade or cancel any time"}</p>
						</div>
					</div>

					<div className={`${styles.subscription_item} ${styles.payAsGo}`}>
						<div className={`${styles.top}`}>
							<h2 className={styles.title}>{dictionary["Pay as you go"] || "Pay as you go"}</h2>
						</div>
						<div className={styles.price} id="payAsGo-credit-cost">
							<div>{price(payAsGo)}</div>
							<div className={styles.priceText}>
								<span className={styles.description}>{dictionary["billed_one_time"] || "billed at one time"}</span>
							</div>
						</div>
						<div className={styles.price_info}>
							<RadioInput {...{ list: payAsGoData, activeItem: payAsGo, handleChange: setPayAsGo, name: "payAsGo", ...RadioInputProps }} />
							{payAsGo.id !== "contactUspayAsGo" ? (
								<button id="payAsGo-submit-btn" className={`${styles.button} ${styles.stroke}`} onClick={payAsGoSubmit}>
									{dictionary["Buy"] || "Buy"}
								</button>
							) : (
								<ContactUsBtn classes={`${styles.button} ${styles.stroke}`} dictionary={dictionary} />
							)}

							<p className={styles.infoMessage}>{dictionary["available_forever"] || "Available Forever: Credits available for use anytime forever"}</p>
						</div>
					</div>
				</div>
				{/* {load && <DynamicMainBlob isBlur={true} />} */}
				{currencySelectionModalIsOpen !== false && (
					<DynamicCurrencySelectionModal
						{...{ setPaymentInRubles, subscribe, paymentInRubles }}
						data={currencySelectionModalIsOpen}
						closeModal={() => {
							setCurrencySelectionModalIsOpen(false);
							setPaymentInRubles(true);
						}}
						locationCurrency={currency.locationCurrency}
					/>
				)}
			</section>
			{openAuthModal && <DynamicAuthModal closeHandler={() => setOpenAuthModal(false)} serviceName="pricing" />}
			{openNotFreeUserModal && (
				<ModalWrapper closeModal={() => setOpenNotFreeUserModal(false)}>
					<div>
						<h2 className={styles.freeUserModalTitle}>{dictionary["you_already_have_a_subscription"] || "You already have a subscription. Are you sure you want to start a new one?"}</h2>
						<div style={{ display: "flex", gap: "20px", justifyContent: "center", marginTop: "48px" }}>
							<button id="pro-submit-btn" className={styles.button} disabled={disableBtn} onClick={() => setOpenNotFreeUserModal(false)}>
								{dictionary["close"] || "Close"}
							</button>
							<button
								id="pro-submit-btn"
								className={`${styles.button} ${styles.stroke}`}
								disabled={disableBtn}
								onClick={() => (openNotFreeUserModal === "pro" ? notFreeUserProSubmit() : notFreeUserPayAsGoSubmit())}
							>
								{dictionary["continue"] || "Continue"}
							</button>
						</div>
					</div>
				</ModalWrapper>
			)}
		</>
	);
}

export default PlansTable;
